import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/header';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

// Share the configured API origin with the store grid and profile
import { API_BASE, imageUrl, productImageUrl } from '../apiConfig';
const MOBILE_BREAKPOINT = 768;

const ProductPage = () => {
  const { city, storeName, productId } = useParams();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    // First fetch store info
    fetch(`${API_BASE}/stores/${storeName}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Store fetch failed! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(storeData => {
        setStore(storeData);
        
        // Then fetch product info
        return fetch(`${API_BASE}/stores/${storeName}/products/${productId}`);
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Product fetch failed! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(productData => {
        console.log('Product data received:', productData);
        setProduct(productData);
        
        // Set the primary image as selected, or first image if no primary
        if (productData.images && productData.images.length > 0) {
          const primaryIndex = productData.images.findIndex(img => img.is_primary);
          setSelectedImageIndex(primaryIndex >= 0 ? primaryIndex : 0);
        }
        
        setLoading(false);
      })
      .catch(err => {
        console.error('Error:', err);
        setError(`Error loading: ${err.message}`);
        setLoading(false);
      });
  }, [city, storeName, productId]);

  const handleExternalLinkClick = () => {
    if (product?.external_url) {
      window.open(product.external_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Helper function to process image URLs
  const processImageUrl = (value, width = 1200) => productImageUrl(value, width) || '/placeholder-image.jpg';

  // Get all available images for the product
  const getProductImages = () => {
    if (product.images && product.images.length > 0) {
      return [...product.images].sort((a, b) => a.display_order - b.display_order);
    } else if (product.image_url) {
      // Fallback to legacy single image
      return [{ 
        id: 'legacy', 
        image_url: product.image_url, 
        is_primary: true, 
        display_order: 0,
        alt_text: product.name 
      }];
    }
    return [];
  };

  // Handle image navigation
  const handlePrevImage = () => {
    const images = getProductImages();
    setSelectedImageIndex(prev => 
      prev > 0 ? prev - 1 : images.length - 1
    );
  };

  const handleNextImage = () => {
    const images = getProductImages();
    setSelectedImageIndex(prev => 
      prev < images.length - 1 ? prev + 1 : 0
    );
  };

  // Handle thumbnail click
  const handleThumbnailClick = (index) => {
    setSelectedImageIndex(index);
  };

  if (loading) return <div className="container text-center py-5">Loading...</div>;
  if (error) return <div className="container text-center py-5 text-danger">{error}</div>;
  if (!product || !store) return <div className="container text-center py-5">Product or store not found</div>;
  
  const images = getProductImages();
  const selectedImage = images[selectedImageIndex];
  const mainImageStyle = isMobile
    ? {
        maxHeight: '30vh',
        height: 'auto',
        aspectRatio: '1 / 1',
        width: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
        objectFit: 'cover',
        cursor: images.length > 1 ? 'pointer' : 'default'
      }
    : {
        height: '400px',
        objectFit: 'cover',
        cursor: images.length > 1 ? 'pointer' : 'default'
      };
  const noImageStyle = isMobile
    ? {
        maxHeight: '30vh',
        height: 'auto',
        aspectRatio: '1 / 1',
        width: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto'
      }
    : {
        height: '400px'
      };
  const renderThumbnailStrip = () => (
    <div className="d-flex gap-2 overflow-auto pb-2">
      {images.map((image, index) => (
        <div
          key={image.id || index}
          className={`thumbnail-container ${index === selectedImageIndex ? 'selected' : ''}`}
          onClick={() => handleThumbnailClick(index)}
          style={{
            cursor: 'pointer',
            minWidth: '80px',
            position: 'relative'
          }}
        >
          <img
            src={processImageUrl(image.image_url, 160)}
            alt={image.alt_text || `${product.name} ${index + 1}`}
            className="img-fluid rounded"
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'cover',
              border: index === selectedImageIndex ? '3px solid #007bff' : '2px solid #dee2e6',
              opacity: index === selectedImageIndex ? 1 : 0.7
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/placeholder-image.jpg';
            }}
          />
        </div>
      ))}
    </div>
  );
  
  return (
    <div className="container py-2 py-md-5">
      <Header />
      
      {/* Store Header with Logo */}
      <div className={`mb-4 ${isMobile ? '' : 'pb-3'} border-bottom`} style={isMobile ? { paddingTop: '0.5rem', paddingBottom: '0.5rem' } : undefined}>
        <Link to={`/${city}/${storeName}`} className="text-decoration-none">
          <div className="d-flex align-items-center">
            {store.profile_image_url ? (
              <img 
                src={store.profile_image_url.startsWith('/') ? 
                  imageUrl(store.profile_image_url) :
                  store.profile_image_url}
                alt={`${store.name} logo`} 
                className="rounded-circle me-3" 
                style={{ width: isMobile ? '32px' : '60px', height: isMobile ? '32px' : '60px', objectFit: 'cover' }}
              />
            ) : (
              <div className="rounded-circle me-3 bg-light d-flex align-items-center justify-content-center" 
                   style={{ width: isMobile ? '32px' : '60px', height: isMobile ? '32px' : '60px', fontSize: isMobile ? '16px' : '24px', color: '#666' }}>
                {store.name?.charAt(0) || ''}
              </div>
            )}
            <h3 className="mb-0 text-dark" style={{ fontSize: isMobile ? '0.95rem' : undefined }}>{store.name}</h3>
          </div>
        </Link>
      </div>

      {isMobile && (
        <h1 className="mb-2" style={{ color: '#1a1a1a', fontSize: '1.1rem' }}>{product.name}</h1>
      )}
      
      <div className="row">
        {/* Image Gallery Section */}
        <div className="col-md-6 order-1">
          {images.length > 0 ? (
            <div className="image-gallery">
              {/* Main Image Display */}
              <div className="position-relative mb-3">
                <img 
                  src={processImageUrl(selectedImage?.image_url)}
                  alt={selectedImage?.alt_text || product.name}
                  className={`img-fluid rounded ${isMobile ? '' : 'w-100'}`}
                  style={mainImageStyle}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/placeholder-image.jpg';
                  }}
                  onClick={() => images.length > 1 && handleNextImage()}
                />
                
                {/* Navigation Arrows (only show if multiple images) */}
                {images.length > 1 && (
                  <>
                    <button 
                      className="btn btn-dark btn-sm position-absolute top-50 start-0 translate-middle-y ms-2"
                      onClick={handlePrevImage}
                      style={{ opacity: 0.8 }}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button 
                      className="btn btn-dark btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
                      onClick={handleNextImage}
                      style={{ opacity: 0.8 }}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                    
                    {/* Image Counter */}
                    <div className="position-absolute bottom-0 end-0 m-3">
                      <span className="badge bg-dark bg-opacity-75">
                        {selectedImageIndex + 1} / {images.length}
                      </span>
                    </div>
                  </>
                )}
              </div>
              
              {/* Thumbnail Strip (only show if multiple images) */}
              {images.length > 1 && !isMobile && renderThumbnailStrip()}
            </div>
          ) : (
            /* No Image Placeholder */
            <div className="d-flex align-items-center justify-content-center bg-light rounded" 
                 style={noImageStyle}>
              <div className="text-center text-muted">
                <i className="fas fa-image fa-3x mb-3"></i>
                <div>No images available</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Product Details Section */}
        <div className="col-md-6 order-2 d-flex d-md-block flex-column">
          <div className="order-1 order-md-1">
            {/* Product Title - MOVED TO TOP */}
            {!isMobile && (
              <h1 className="mb-2 mb-md-3" style={{ color: '#1a1a1a' }}>{product.name}</h1>
            )}
            
            {/* Breadcrumb - MOVED BELOW TITLE */}
            <nav aria-label="breadcrumb" className="d-none d-md-block mb-3">
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link to={`/${city}/${storeName}`}>{store.name || storeName}</Link></li>
                <li className="breadcrumb-item active">{product.name}</li>
              </ol>
            </nav>
          </div>
          
          <div className="order-2 order-md-2">
            <p className="lead mb-2 mb-md-3">{product.price_type === 'from' ? 'From ' : ''}${parseFloat(product.price).toFixed(2)}</p>
            {product.availability === 'out_of_stock' && <p className="text-muted">Sold out online</p>}
            {product.availability === 'preorder' && <p className="text-muted">Preorder</p>}
          </div>
          
          <div className="order-4 order-md-3">
            {/* Render description with markdown support */}
            <div className="product-description mb-4">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  // Custom styling for markdown elements
                  h1: ({node, ...props}) => <h3 className="mt-3 mb-2" {...props} />,
                  h2: ({node, ...props}) => <h4 className="mt-3 mb-2" {...props} />,
                  h3: ({node, ...props}) => <h5 className="mt-2 mb-2" {...props} />,
                  p: ({node, ...props}) => <p className="mb-2" {...props} />,
                  ul: ({node, ...props}) => <ul className="mb-3" {...props} />,
                  ol: ({node, ...props}) => <ol className="mb-3" {...props} />,
                  li: ({node, ...props}) => <li className="mb-1" {...props} />,
                  strong: ({node, ...props}) => <strong className="fw-bold" {...props} />,
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-start border-4 border-primary ps-3 my-3 text-muted fst-italic" {...props} />
                  ),
                  code: ({node, inline, ...props}) => 
                    inline ? (
                      <code className="bg-light px-1 rounded" {...props} />
                    ) : (
                      <pre className="bg-light p-2 rounded overflow-auto"><code {...props} /></pre>
                    ),
                  a: ({node, ...props}) => <a className="text-primary" {...props} />,
                  hr: ({node, ...props}) => <hr className="my-3" {...props} />,
                  table: ({node, ...props}) => <table className="table table-bordered my-3" {...props} />,
                }}
              >
                {product.description || '*No description available*'}
              </ReactMarkdown>
            </div>
          </div>
          
          <div className="order-5 order-md-4">
            {/* Image Info (for debugging/information) */}
            {images.length > 0 && (
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="mb-2">
                  <i className="fas fa-images me-2"></i>
                  Image Gallery ({images.length} image{images.length !== 1 ? 's' : ''})
                </h6>
                <small className="text-muted">
                  {images.length > 1 ? 
                    'Click on thumbnails or use arrow buttons to view all images' : 
                    'Single image available'}
                </small>
              </div>
            )}
          </div>
          
          {images.length > 1 && isMobile && (
            <div className="order-6 order-md-6">
              {renderThumbnailStrip()}
            </div>
          )}

          <div className="order-3 order-md-5 mb-2 mb-md-3">
            <div className="d-grid gap-2">
              {product.external_url ? (
                <button 
                  className={`btn btn-primary ${isMobile ? '' : 'btn-lg'}`}
                  onClick={handleExternalLinkClick}
                >
                  View Item
                </button>
              ) : (
                <button className={`btn btn-outline-secondary ${isMobile ? '' : 'btn-lg'}`} disabled>
                  Contact Store
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
